import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalConfig } from 'src/app/shared/components/modal/modal-config';
import { ParametroLista, TipoParametro } from '../../../data-visualization/reports/models/parametro';
import { TIPO_PARAMETRO_LIST } from 'src/app/util/constantes';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { Observable, delay, finalize, take, takeWhile, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HelperFunctions } from 'src/app/util/util-functions';
import { CustomValidators } from 'src/app/util/custom-validators';
import { CustomInput } from 'src/app/shared/components/type-input/custom-input';
import { PARAMETERS_LIST_MESSAGES } from 'src/app/util/constantes-mensajes';
import { ParameterListService } from '../parameter-list.service';

@Component({
  selector: 'app-manage-parameter-list',
  templateUrl: './manage-parameter-list.component.html',
  styleUrls: ['./manage-parameter-list.component.scss']
})
export class ManageParameterListComponent implements OnInit, OnDestroy {
  // Parameter list to edit
  @Input() parameterList: ParametroLista;
  // Parameter list form group
  parameterListForm: FormGroup;
  // Variable to set modal information
  public modalConfig: ModalConfig;
  // Types of parameters
  parameterType: typeof TipoParametro = TipoParametro;
  // Constant parameter types
  types = TIPO_PARAMETRO_LIST;
  // Source list to be displayed in select
  sources$: Observable<string[]>;
  // Icons
  faCircleXmark = faCircleXmark;
  // Variable to unsubscribe pending subscriptions
  isAlive: boolean = true;
  // Variable to show/hide spinner
  isLoading: boolean = false;

  constructor(private parameterListService: ParameterListService, private ngbActiveModal: NgbActiveModal) { }

  ngOnInit(): void {
    this.modalConfig = {
      modalTitle: this.parameterList ? "Editar parametro lista" : "Crear parametro Lista",
      ...this.modalConfig,
    };
    this.prepareData();
    this.initForm();
    this.updateTypeStatus();
    this.updateSourceStatus();
  }

  /**
   * Gets the auxiliar data to show in select lists
   * @returns {none}
   */
  prepareData(): void {
    this.sources$ = this.parameterListService.getParameterSources().pipe(take(1));
  }

  /**
  * A function that inits the parameterForm group
  * @returns {none}
  */
  initForm(): void {
    const isSourceType: FormControl = new FormControl(this.parameterList?.origen ? true : false);
    const hasType = this.parameterList?.tipoParametro;
    const values = this.parameterList?.valores || [];
    const source = this.parameterList?.origen;
    this.addSource(source)
    this.parameterListForm = new FormGroup({
      uuid: new FormControl(this.parameterList?.uuid || uuidv4()),
      name: new FormControl(this.parameterList?.nombre || null, [Validators.required, Validators.maxLength(255)]),
      type: new FormControl({ value: hasType || TipoParametro.string, disabled: !!hasType }, Validators.required),
      isSourceType,
      source: new FormControl(source, CustomValidators.otherControlsRequired<boolean>([
        { referenceControl: isSourceType, comparableValue: true }
      ])),
      options: new FormArray(values.map((option: string) => new FormControl(option)), CustomValidators.otherControlsRequired<boolean>([
        { referenceControl: isSourceType, comparableValue: false }
      ]))
    });
  }

  /**
* A function that adds a source to the list
* @returns {none}
*/
  addSource(source?: string) {
    if (source) {
      this.sources$ = this.sources$.pipe(tap(sources => {
        sources.push(source);
      }));
    }
  }

  /**
  * A function that updates the options values and validators
  * @returns {none}
  */
  updateTypeStatus(): void {
    this.getControl("type")!.valueChanges
      .pipe(takeWhile(() => this.isAlive)).subscribe(() => {
        if (this.hasOptions()) {
          HelperFunctions.resetFormArray(this.options);
        }
      });
  }

  /**
  * A function that updates the options values and validators
  * @returns {none}
  */
  updateSourceStatus(): void {
    this.getControl("isSourceType")?.valueChanges
      .pipe(takeWhile(() => this.isAlive)).subscribe(() => {
        this.getControl("options")?.updateValueAndValidity();
        this.getControl("source")?.updateValueAndValidity();
      });
  }

  /**
   * A function that create a customInput for options
   * @returns {CustomInput}
   */
  getOptionInput(): CustomInput {
    const type: string = this.getControl('type')?.value;
    const customInput: CustomInput = {
      id: type,
      type,
      label: "Valores",
      isRow: true,
      required: true,
      style: "width:100%;"
    }
    return customInput;
  }

  /**
  * A function that gets the control of parameterListGroup
  * @param {string} name
  * @returns {FormControl}
  */
  getControl(name: string): FormControl {
    return this.parameterListForm.get(name) as FormControl;
  }

  /**
  * A function that gets the formarray of parameterlist
  * @returns {FormArray}
  */
  get options(): FormArray {
    return this.parameterListForm.get('options') as FormArray;
  }

  /**
  * A function that checks if the control has values
  * @returns {boolean}
  */
  hasOptions(): boolean {
    const options = this.options?.value;
    return options.length > 0;
  }

  /**
  * A function that checks if the control is invalid
  * @param {string} name
  * @returns {boolean}
  */
  hasErrors(name: string): boolean {
    const control = this.parameterListForm.get(name) as FormControl;
    return HelperFunctions.controlIsInvalid(control);
  }

  /**
   * A function that save the parameter list info
   * @param {ParametroLista} parameterList
   * @returns {none}
   */
  saveParameterList(parameterList: ParametroLista): void {
    this.isLoading = true;
    const subscription = this.parameterList ?
      this.parameterListService.updateParameterList(parameterList) :
      this.parameterListService.createParameterList(parameterList);
    subscription
      .pipe(
        delay(500),
        finalize(() => this.isLoading = false))
      .subscribe({
        next: (response: ParametroLista) => {
          this.ngbActiveModal.close({ message: PARAMETERS_LIST_MESSAGES.CREATE.PARAMETER_LIST, data: response, confirmTitle: "Aceptar" });
        },
        error: (error) => {
          this.ngbActiveModal.close({ message: error.error.message, confirmTitle: "Cerrar", isError: true });
        }
      })
  }

  /**
  * A function that submit the parameter list
  * @returns {none}
  */
  onSubmit(): void {
    if (this.parameterListForm.valid) {
      const param = this.parameterListForm.getRawValue();
      const parameterList: ParametroLista = {
        uuid: param.uuid,
        nombre: param.name,
        tipoParametro: param.type,
        origen: param.isSourceType ? param.source : null,
        valores: !param.isSourceType ? param.options : null
      }
      this.saveParameterList(parameterList);
    } else {
      this.parameterListForm.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
